import { useCallback, useEffect, useRef, useState } from 'react';

import RefreshIcon from '@atlaskit/icon/core/refresh';
import { useDynamicConfig } from '@trello/dynamic-config';
import { useFeatureGate } from '@trello/feature-gate-client';
import { dismissFlag, showFlag } from '@trello/nachos/experimental-flags';

const DURATION_IN_MS = {
  ONE_SECOND: 1000,
  ONE_MINUTE: 60000,
  ONE_HOUR: 3600000,
  ONE_DAY: 86400000,
  ONE_WEEK: 604800000,
};

export const getDelay = (duration: number, elapsed: number) => {
  const { ONE_HOUR, ONE_SECOND } = DURATION_IN_MS;
  const remaining = duration - elapsed;
  const interval = Math.round(remaining / 2);
  return Math.min(Math.max(interval, ONE_SECOND), ONE_HOUR);
};

export const useUpdateNudge = () => {
  const timerRef = useRef<number | null>(null);
  const [initTimestamp, setInitTimestamp] = useState<number | null>(null);

  const { value: isEnabled } = useFeatureGate('phx_update_nudge');

  const timeInterval = useDynamicConfig<number>(
    'trello_web_update_nudge_time_interval',
  );

  const onClick = useCallback(() => {
    // TODO: add analytics
    window.location.reload();
  }, []);

  const maybeNudge = useCallback(() => {
    if (timerRef.current) {
      window.clearTimeout(timerRef.current);
    }

    if (!initTimestamp || !isEnabled) {
      return;
    }

    const timeSinceInit = Date.now() - initTimestamp;
    const delay = getDelay(timeInterval, timeSinceInit);

    if (timeSinceInit > timeInterval) {
      // TODO: finalize copy * i18n
      // TODO: add analytics
      showFlag({
        id: 'update-nudge',
        icon: <RefreshIcon label="" spacing="spacious" />,
        title: 'New version available',
        description:
          'A new version of Trello is available. Please update to get the latest and greatest.',
        isUndismissable: true,
        actions: [
          {
            content: 'Update now',
            type: 'link',
            onClick,
          },
        ],
      });
      return;
    }

    timerRef.current = window.setTimeout(maybeNudge, delay);
  }, [initTimestamp, isEnabled, timeInterval, onClick]);

  useEffect(() => {
    setInitTimestamp(Date.now());
  }, []);

  useEffect(() => {
    maybeNudge();

    return () => {
      dismissFlag({ id: 'update-nudge' });
      if (timerRef.current) {
        window.clearTimeout(timerRef.current);
      }
    };
  }, [maybeNudge]);
};
